import { useCallback } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { Portal, Project } from 'common/contracts';

import {
  EApiRoutes,
  getApiRouteOrMock,
  getCustomHeader
} from '../../../api/api.utils';
import { useUserDetails } from '../../../api/useUserDetails';
import { EToasterStatus, showToast } from '../../../utils/showToast';
import { ProjectCategory } from '../constants';

export function useProjectUpdater() {
  const { userDetails } = useUserDetails();
  const queryClient = useQueryClient();

  const updateThumbnailImage = useCallback(
    (itemId: string, category: ProjectCategory, thumbnailImageUrl: string) => {
      if (!userDetails) return;

      const updatedUserDetails = structuredClone(userDetails);

      if (category === ProjectCategory.PROJECT && updatedUserDetails.projects) {
        const projectIndex = updatedUserDetails.projects.findIndex(
          (project: Project) => project.publisherId === itemId
        );
        if (projectIndex !== -1) {
          updatedUserDetails.projects[projectIndex].thumbnailImageUrl =
            thumbnailImageUrl;
        }
      } else if (
        category === ProjectCategory.PORTAL &&
        updatedUserDetails.portals
      ) {
        const portalIndex = updatedUserDetails.portals.findIndex(
          (portal: Portal) => portal.portalId === itemId
        );
        if (portalIndex !== -1) {
          updatedUserDetails.portals[portalIndex].thumbnailImageUrl =
            thumbnailImageUrl;
        }
      }

      // Update React Query cache instead of localStorage
      queryClient.setQueryData([EApiRoutes.USERS], updatedUserDetails);
    },
    [userDetails, queryClient]
  );

  const updateProjectTags = useCallback(
    async (itemId: string, category: ProjectCategory, tags: string[]) => {
      if (!userDetails) return;

      const updatedUserDetails = structuredClone(userDetails);
      let publisherId: string | undefined;
      let portalId: string | undefined;

      if (category === ProjectCategory.PROJECT && updatedUserDetails.projects) {
        const projectIndex = updatedUserDetails.projects.findIndex(
          (project: Project) => project.publisherId === itemId
        );

        if (projectIndex !== -1) {
          updatedUserDetails.projects[projectIndex].tags = tags;
          publisherId = updatedUserDetails.projects[projectIndex].publisherId;
        }
      } else if (
        category === ProjectCategory.PORTAL &&
        updatedUserDetails.portals
      ) {
        const portalIndex = updatedUserDetails.portals.findIndex(
          (portal: Portal) => portal.portalId === itemId
        );

        if (portalIndex !== -1) {
          updatedUserDetails.portals[portalIndex].tags = tags;
          portalId = updatedUserDetails.portals[portalIndex].portalId;
        }
      }

      queryClient.setQueryData([EApiRoutes.USERS], updatedUserDetails);

      if (publisherId || portalId) {
        try {
          if (publisherId) {
            await axios.put(
              getApiRouteOrMock(EApiRoutes.PUBLISHER),
              {
                publisherId,
                tags
              },
              {
                headers: getCustomHeader(publisherId, undefined)
              }
            );
          } else if (portalId) {
            await axios.put(
              getApiRouteOrMock(EApiRoutes.PORTAL_SETTINGS),
              {
                tags
              },
              {
                headers: { 'x-portal': portalId }
              }
            );
          }

          showToast({
            message: 'Tags updated successfully',
            status: EToasterStatus.SUCCESS
          });
        } catch (error) {
          showToast({
            message: 'Error updating tags',
            status: EToasterStatus.ERROR
          });
        }
      }
    },
    [userDetails, queryClient]
  );

  return {
    updateThumbnailImage,
    updateProjectTags
  };
}
